import React from 'react';
import { Link, resolvePath } from 'react-router';
import { Opportunity, CandidateOpportunitiesConst } from '@axiom/const';
import type {
  Candidate,
  CandidateWorkFeedItem,
  Experience,
  OpportunityCandidate,
} from '@axiom/validation';
import moment from 'moment';
import {
  CandidateTaxonomyUtil,
  CondensedLarge,
  ToastUtil,
  CondensedHeader,
} from '@axiom/ui';

import { EnvUtil } from './env-util';
import { LocationUtil } from './location-util';

const {
  CandidateStatuses: {
    Submitted,
    Completed,
    Engaged,
    Interviewing,
    RepBioShare,
    Selected,
    Suggested,
    Warmed,
    WarmedYes,
    InterestedRejected,
    Cooled,
    Rejected,
    TalentOptOut,
    Interested,
    Removed,
    ShortList,
  },
} = CandidateOpportunitiesConst;

export const WORK_FEED_VIEWS = {
  all: 'all',
  saved: 'saved',
  spv: 'spv',
} as const;

export type WorkFeedViewsType =
  (typeof WORK_FEED_VIEWS)[keyof typeof WORK_FEED_VIEWS];

export const WorkFeedUtil = {
  isUSBased: (opportunity: CandidateWorkFeedItem['opportunity']) => {
    const location = opportunity.locationCityStateCountry ?? '';
    const [country] = location.split(', ').slice(-1);
    return country === 'US';
  },
  getLanguagesDisplay: (opportunity: CandidateWorkFeedItem['opportunity']) => {
    const oppLanguages = opportunity.languages ?? [];
    const langSet = oppLanguages.reduce((acc, curr) => {
      if (curr.language?.name) {
        acc.add(curr.language.name);
      }
      return acc;
    }, new Set<string>());
    return [...langSet].join(', ');
  },
  getWorkFeedCandidateStatuses: (workfeedItem: CandidateWorkFeedItem) => {
    const { CLOSED_LOST, CLOSED_WON, CONTRACT } = Opportunity;
    const { candidateIsInterested, candidateStatus, opportunity } =
      workfeedItem;
    const interestedStateSuggestedStatus =
      candidateStatus === Suggested && candidateIsInterested;
    const interestedStateShortlistedStatus =
      candidateStatus === ShortList && candidateIsInterested;

    const submittedCTAStatuses = [
      Submitted,
      Completed,
      Engaged,
      Interviewing,
      RepBioShare,
      Selected,
      Warmed,
      WarmedYes,
    ];

    const removeCTAStatuses = [
      InterestedRejected,
      Cooled,
      Rejected,
      TalentOptOut,
      Removed,
    ];

    const specialStatusesOverStage = [Selected, Engaged, Completed];

    return {
      isInterested:
        candidateStatus === Interested ||
        interestedStateSuggestedStatus ||
        interestedStateShortlistedStatus,
      isSubmitted: submittedCTAStatuses.includes(candidateStatus),
      isClosed:
        opportunity.stage === CLOSED_WON ||
        opportunity.stage === CLOSED_LOST ||
        removeCTAStatuses.includes(candidateStatus),
      isSelected: specialStatusesOverStage.includes(candidateStatus),
      isFulfillmentCompleted: opportunity.stage === CONTRACT,
    };
  },
  getView: (path: string): WorkFeedViewsType => {
    return path.toLowerCase().includes(WORK_FEED_VIEWS.saved)
      ? WORK_FEED_VIEWS.saved
      : WORK_FEED_VIEWS.all;
  },
  isAllView: (currView: WorkFeedViewsType) => {
    return currView.toLowerCase().includes(WORK_FEED_VIEWS.all);
  },
  isSavedView: (currView: WorkFeedViewsType) => {
    return currView.toLowerCase().includes(WORK_FEED_VIEWS.saved);
  },
  isSinglePageView: (currView: WorkFeedViewsType) => {
    return currView.toLowerCase().includes(WORK_FEED_VIEWS.spv);
  },
  formatPhaseForDisplay: (
    phase: CandidateWorkFeedItem['phase']
  ): CandidateWorkFeedItem['phase'] | 'Submitting' => {
    switch (phase) {
      case 'Submitted': {
        return 'Submitting';
      }
      default: {
        return phase;
      }
    }
  },
  getQualificationWarnings: (
    candidate: Candidate,
    workFeedItem: CandidateWorkFeedItem,
    candidateOpportunities: OpportunityCandidate[],
    experiences: Experience[] = []
  ) => {
    const { position, opportunity } = workFeedItem;
    const warnings = [];

    if (
      opportunity.countryCode &&
      candidate.addressCountryCode &&
      LocationUtil.qualifyCountryCode(candidate.addressCountryCode) !==
        LocationUtil.qualifyCountryCode(opportunity.countryCode)
    ) {
      warnings.push('Location or time zone');
    }

    const candidateFocusAreas = CandidateTaxonomyUtil.getCandidateFocusAreas({
      experiences,
      primaryFocusArea: candidate.primaryFocusArea,
    });
    if (
      opportunity.primaryFocusArea &&
      !candidateFocusAreas.includes(opportunity.primaryFocusArea)
    ) {
      warnings.push('Focus area');
    }

    if (opportunity.languages?.length && candidate.languages?.length) {
      const candidateLanguageSet = new Set(
        candidate.languages.map(candidateLanguage => candidateLanguage.name)
      );
      const languagesIntersections = opportunity.languages.filter(
        opportunityLanguage =>
          candidateLanguageSet.has(opportunityLanguage.language?.name)
      );
      if (languagesIntersections.length === 0) {
        warnings.push('Language');
      }
    }

    if (
      candidate.businessTitle &&
      position.businessTitle &&
      position.businessTitle !== candidate.businessTitle
    ) {
      warnings.push('Legal role');
    }

    if (candidate.weeklyAvailability !== null && position.billingHoursPerWeek) {
      const isCandidateRollOffImminent = () => {
        if (!candidate.soonestEngagementEndDate) return false;

        const daysUntilRollOff = moment(
          candidate.soonestEngagementEndDate
        ).diff(moment(new Date()), 'days');

        return daysUntilRollOff >= 0 && daysUntilRollOff <= 30;
      };

      const getPositionByEndDate = (
        opportunities: OpportunityCandidate[],
        endDate: string | null
      ) => {
        if (opportunities.length === 0) return null;

        for (const candidateOpportunity of opportunities) {
          if (
            candidateOpportunity.candidateStatus === Engaged &&
            candidateOpportunity.position?.endDate === endDate
          ) {
            return candidateOpportunity.position;
          }
        }
        return null;
      };

      const shouldWarnWeeklyAvailability = () => {
        const rollOffPosition = getPositionByEndDate(
          candidateOpportunities,
          candidate.soonestEngagementEndDate
        );

        return isCandidateRollOffImminent() && rollOffPosition
          ? (candidate.weeklyAvailability ?? 0) +
              (rollOffPosition.billingHoursPerWeek ?? 0) <
              (position.billingHoursPerWeek ?? 0)
          : (candidate.weeklyAvailability ?? 0) <
              (position.billingHoursPerWeek ?? 0);
      };

      if (shouldWarnWeeklyAvailability()) {
        warnings.push('Weekly commitment');
      }
    }

    return warnings;
  },
  addInterestedToasts: (interestedCount: number, targetLink: string) => {
    if (interestedCount === EnvUtil.workFeedMaxInterestedLimit - 1) {
      ToastUtil.add({
        children: (
          <div>
            <CondensedHeader>Limit Reached</CondensedHeader>
            <CondensedLarge>
              Axiom can consider you for up to{' '}
              {EnvUtil.workFeedMaxInterestedLimit} opportunities at a time.
            </CondensedLarge>
          </div>
        ),
        type: 'error',
        name: 'LIMIT_REACHED',
      });
    }

    ToastUtil.add({
      children: (
        <div>
          <CondensedHeader>Interest Expressed</CondensedHeader>
          <CondensedLarge>
            This opportunity will now be tracked on{' '}
            <Link
              data-test="ENGAGEMENTS_LINK"
              onClick={() => {
                ToastUtil.removeAll();
              }}
              to={resolvePath(targetLink, window.location.pathname)}
            >
              Engagements {'>'} Prospective Opportunities.
            </Link>
          </CondensedLarge>
        </div>
      ),
      name: 'EXPRESS_INTEREST_SUCCESS',
    });
  },
  addSavedToasts: (targetLink: string) => {
    ToastUtil.add({
      children: (
        <div>
          <CondensedHeader>Opportunity Saved</CondensedHeader>
          <CondensedLarge>
            This opportunity has been moved to your{' '}
            <Link
              data-test="SAVED_TAB_LINK"
              onClick={() => {
                ToastUtil.removeAll();
              }}
              to={resolvePath(targetLink, window.location.pathname)}
            >
              Saved tab
            </Link>
            . We will alert you when this opportunity is ready to accept
            expressions of interest.
          </CondensedLarge>
        </div>
      ),
      name: 'OPPORTUNITY_SAVED_TOAST',
    });
  },
  addErrorToast: (email?: string | null) => {
    ToastUtil.add({
      children: (
        <div>
          <CondensedHeader>We need more information</CondensedHeader>
          <CondensedLarge>
            To complete your submission, contact your{' '}
            <a
              data-test="TALENT_SUCCESS_LINK"
              href={`mailto:${email || EnvUtil.axiomAnswersEmail}`}
            >
              Talent Success Partner
            </a>
          </CondensedLarge>
        </div>
      ),
      name: 'EXPRESS_INTEREST_ERROR',
      type: 'error',
      dismissible: true,
    });
  },
  isUnavailableToCandidate: (workFeedItem: CandidateWorkFeedItem) => {
    const { isSubmitted, isSelected } =
      WorkFeedUtil.getWorkFeedCandidateStatuses(workFeedItem);
    return (
      workFeedItem.isNotAvailableOnCandidateFeed && !isSubmitted && !isSelected
    );
  },
  hasRateValuesForSelfService: (candidate: Candidate) => {
    return (
      candidate.currencyType != null &&
      candidate.hourlyCompensation != null &&
      candidate.compHourlyPremium != null &&
      candidate.compHourlyPremium >= 0 &&
      candidate.compHourly40hr != null &&
      candidate.compHourly40hr >= 0
    );
  },
};
